/**
 * جزئیاتِ یک کاندیدا — همان چیزی که زیرِ ردیف باز می‌شود.
 *
 * ترتیبِ بخش‌ها از «چه می‌شود» به «چه شد» به «چرا» می‌رود:
 *   ۰. غربالگری + تست — «قبول شده؟» و «الان چه درمی‌آید؟»، کنارِ هم
 *   ۱. رجیستری — امروز چه چیزی اجرا می‌شود، و تاریخچه‌اش
 *   ۲. غربالگری (کامل) — حکمِ هر آدرس، با یادداشت‌هایش
 *   ۳. پیش‌نویس — از کجا آمد، مدل چه گفت
 *   ۴. سندها — واقعاً چه چیزی گرفته می‌شود
 *
 * ⚠️ **بخشِ ۰ بالای همه است و دو نیم دارد، عمداً.** دو سؤالی که آدم
 * پشتِ سرِ هم می‌پرسد این دوتاست — «این منبع قبول شده؟» (غربالگری) و
 * «الان چه چیزی ازش درمی‌آید؟» (تست) — و تا وقتی دو بخش با هم فاصله
 * داشتند، جوابِ «فرقِ بررسی و تست چیست» از خودِ صفحه درنمی‌آمد. بقیه‌ی
 * بخش‌ها تاریخ‌اند و پایین می‌مانند.

 * ⚠️ بخشِ ۲ همان حکم را **به‌ازای هر آدرس** نشان می‌دهد. تکرار نیست:
 * بالا خلاصه‌ی تصمیم است، پایین شواهدش.
 *
 * ⚠️ جدا از کوئریِ جدول است و نه چند ستونِ بیشتر روی آن: این‌ها به‌ازای هر
 * ردیف چند کوئری‌اند و کشیدنشان برای سیصد ردیفی که هیچ‌وقت باز نمی‌شوند،
 * صفحه را کند می‌کند بدونِ اینکه چیزی نشان بدهد.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sourceRepo, type InspectionRow, type SourceVersionRow } from '@/core/data/sourceRepo';
import {
  rejectionReason, TODO_HELP, TODO_LABEL, type PipelineRow, type TodoKind,
} from '@/core/lib/sourcePipeline';
import { reportError } from '@/core/lib/report';
import { Modal } from '@/ui/Modal';
import { SourceEditor } from './SourceEditor';
import { DraftEditor } from './DraftEditor';
import { RejectDialog } from './RejectDialog';
import { TodoDialog } from './TodoDialog';
import { SourceTestPanel } from './SourceTestPanel';

const when = (iso: string | null) =>
  (iso ? new Date(iso).toISOString().slice(0, 16).replace('T', ' ') : '—');

function Urls({ urls }: { urls: string[] }) {
  return (
    <ul style={{ listStyle: 'none', margin: 0 }}>
      {urls.map((u) => (
        <li key={u}>
          <a className="mono" href={u} target="_blank" rel="noreferrer">{u}</a>
        </li>
      ))}
    </ul>
  );
}

/**
 * حکمِ غربالگری، بالای پنلِ تست — «قدمِ صفر».
 *
 * ═══ چرا این‌جا تکرار شده ═══
 *
 * همین حکم پایین‌تر در بخشِ «غربالگری» هم هست، با جزئیاتِ هر آدرس. ولی
 * دو سؤالی که آدم پشتِ سرِ هم می‌پرسد این دوتاست — «این منبع اصلاً قبول
 * شده؟» و «الان چه چیزی ازش درمی‌آید؟» — و جوابشان دو بخش با هم فاصله
 * داشت. نتیجه‌اش سردرگمیِ واقعی بود: «فرقِ بررسی و تست چیست و کدام را
 * بزنم؟»
 *
 * ⚠️ **کوئریِ تازه‌ای نمی‌زند.** هر چهار عددش از همان ردیفِ جدول می‌آید
 * که از قبل در دست است. تکرارِ داده در دو جای صفحه اشکالی ندارد؛ تکرارِ
 * یک رفت‌وبرگشت به دیتابیس دارد.
 */
function Gates({ row }: { row: PipelineRow }) {
  const reason = rejectionReason(row);

  // ⚠️ `null` یعنی «هنوز بررسی نشده» و با «رد شد» یکی نیست — همان تمایزی
  // که در جدول هم با `—` نشان داده می‌شود. یکی کردنشان یعنی ردیفی که فقط
  // منتظرِ نوبتش است، شکست‌خورده به نظر برسد.
  if (row.looks_usable == null) {
    return (
      <p className="note">
        هنوز غربال نشده. لازم نیست کاری بکنی — زنجیره‌ی شبانه هر شب تا ۲۰
        پیش‌نویس را خودش بررسی می‌کند. دکمه‌ی «بررسی» فقط برای وقتی است که
        عجله داری.
      </p>
    );
  }

  return (
    <>
      <div className="chiprow">
        <span className={row.looks_usable ? 'chip on' : 'chip warn'}>
          {row.looks_usable ? '✅' : '❌'} استخراج
        </span>
        <span className={row.has_events ? 'chip on' : 'chip warn'}>
          {row.has_events ? '✅' : '❌'} رویداد
        </span>
        <span className="dim" style={{ fontSize: 12 }}>
          <span className="mono">{row.verdict ?? '—'}</span>
          {' / '}
          <span className="mono">{row.listing_verdict ?? '—'}</span>
          {' · '}{row.urls_checked} آدرس بررسی شد
          {' · '}{when(row.inspected_at)}
        </span>
      </div>

      {/*
        یک جمله‌ی توضیح، چون معنیِ این دو گیت شهودی نیست: اولی «با
        خواننده‌های امروزِ ما خواندنی است؟» و دومی «رویدادِ آینده دارد یا
        بایگانی است؟». نبودِ هرکدام ترفیع را متوقف می‌کند.
      */}
      <p className="dim" style={{ fontSize: 12, marginTop: 4 }}>
        این دو، دروازه‌ی ترفیع‌اند: «استخراج» یعنی صفحه با خواننده‌های امروز
        خواندنی است، «رویداد» یعنی رخدادِ پیشِ‌رو دارد و بایگانی نیست. تست
        پایین جوابِ سؤالِ دیگری را می‌دهد — «الان دقیقاً چه چیزی درمی‌آید؟»
      </p>

      {reason ? <p className="err" style={{ fontSize: 12 }}>ردِ ترفیع: {reason}</p> : null}
    </>
  );
}

export function SourceDetail({ row, testNow, seenReasons, seenTodoNotes, onQueued }: {
  row: PipelineRow;
  /** یادداشت‌های قبلیِ صفِ استخراجگر — تا هر بار از نو تایپ نشوند. */
  seenTodoNotes?: (string | null | undefined)[];
  /** `promote_outcome`ِ همه‌ی ردیف‌های جدول — خوراکِ فهرستِ دلایلِ قبلی. */
  seenReasons?: (string | null | undefined)[];
  /** دکمه‌ی «تست»ِ ردیفِ جدول این را می‌دهد: باز شو و همان لحظه تست کن. */
  testNow?: boolean;
  onQueued: (message: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editingDraft, setEditingDraft] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [filing, setFiling] = useState(false);
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ['srcDetail', row.city, row.bare],
    queryFn: () => sourceRepo.detail(row.city, row.bare),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['srcPipeline'] });
    qc.invalidateQueries({ queryKey: ['srcDetail'] });
  };

  /**
   * حکمِ دستی روی پیش‌نویس — فوری، بی صف.
   *
   * ⚠️ برخلافِ خاموش کردنِ منبع (که رجیستری را می‌نویسد و در صف می‌نشیند)،
   * این یکی فقط یک ستون در `crawler.source_drafts` است و همان لحظه اثر
   * می‌کند — همان استدلالِ `addDraft`: پیش‌نویس هیچ‌کدام از نامتغیرهای
   * رجیستری را ندارد.
   */
  const verdict = useMutation({
    mutationFn: (reason: string | null) =>
      sourceRepo.setDraftVerdict(detail.data?.draft?.draft_id ?? '', reason),
    onSuccess: (_r, reason) => {
      refresh();
      onQueued(reason ? `ردشد: ${reason}` : 'به صفِ پیش‌نویس‌ها برگشت.');
    },
    onError: (err) => {
      reportError('admin.sources.draftVerdict', err);
      onQueued(err instanceof Error ? err.message : String(err));
    },
  });

  /**
   * فرستادن به صفِ «کارِ استخراجگر» یا بستنش.
   *
   * ⚠️ شناسه‌ی آخرین تست همراهش می‌رود تا شواهد کپی شود — نه ارجاع، چون
   * فقط پنج اجرای آخر می‌ماند و ارجاع به اجرایی که پاک می‌شود یعنی ردیفی
   * که دلیلش را از دست داده.
   */
  const file = useMutation({
    mutationFn: (v: { kind: TodoKind; note: string }) =>
      sourceRepo.todo(row.draft_id ?? row.bare, v.kind, v.note, row.last_test?.id ?? null),
    onSuccess: (_r, v) => { refresh(); onQueued(`${TODO_LABEL[v.kind]}: ${v.note}`); },
    onError: (err) => {
      reportError('admin.sources.todo', err);
      onQueued(err instanceof Error ? err.message : String(err));
    },
  });

  const closeTodo = useMutation({
    mutationFn: (id: number) => sourceRepo.todoClose(id),
    onSuccess: () => { refresh(); onQueued('از صفِ استخراجگر بیرون رفت.'); },
    onError: (err) => reportError('admin.sources.todoClose', err),
  });

  /**
   * روشن/خاموش کردنِ منبعِ فعال.
   *
   * ⚠️ **در صف می‌نشیند و فوری نیست** — `enabled` فیلدِ رفتاری است، یعنی
   * نسخه‌ی تازه‌ای در رجیستری می‌سازد و آن کار فقط از پایتون درست
   * انجام‌شدنی است (هشِ رفتاری). همان مسیرِ «ویرایش»، فقط با یک کلیک.
   */
  const power = useMutation({
    mutationFn: (next: boolean) =>
      sourceRepo.queue('edit_source', row.bare, { enabled: next }),
    onSuccess: (_j, next) => {
      refresh();
      onQueued(next
        ? `روشن کردنِ ${row.bare} در صف نشست.`
        : `خاموش کردنِ ${row.bare} در صف نشست.`);
    },
    onError: (err) => {
      reportError('admin.sources.power', err);
      onQueued(err instanceof Error ? err.message : String(err));
    },
  });

  if (detail.isLoading) return <p className="dim">…</p>;
  if (detail.isError) {
    return <p className="err">{(detail.error as Error)?.message}</p>;
  }

  const d = detail.data!;
  const active = d.versions.find((v) => v.active) ?? null;
  const payload = (active?.payload ?? {}) as Record<string, unknown>;
  const urls = Array.isArray(payload.urls) ? (payload.urls as string[]) : [];

  return (
    <div className="detail">
      {/* ── ۰ ــ حکمِ غربالگری، بعد تست ────────────────── */}
      <h3>غربالگری</h3>
      <Gates row={row} />

      <h3 style={{ marginTop: 12 }}>تست</h3>
      <SourceTestPanel target={row.draft_id ?? row.bare} autoStart={testNow}
        onQueued={onQueued} />

      {/*
        ⚠️ صفِ استخراجگر زیرِ تست است و نه بالای آن، عمداً: این تصمیم فقط
        بعد از خواندنِ نتیجه معنی دارد. دکمه‌ای که بالای شواهد بنشیند،
        دعوت به حدس زدن است.
      */}
      <div className="chiprow" style={{ marginTop: 8 }}>
        {row.todo ? (
          <>
            <span className="chip warn" title={TODO_HELP[row.todo.kind]}>
              {TODO_LABEL[row.todo.kind]}
            </span>
            {row.todo.note ? <span className="dim">{row.todo.note}</span> : null}
            <button className="linkbtn" type="button" disabled={closeTodo.isPending}
              onClick={() => closeTodo.mutate(row.todo!.id)}>کد نوشته شد</button>
            <button className="linkbtn" type="button"
              onClick={() => setFiling(true)}>عوضش کن</button>
          </>
        ) : (
          <button className="btn ghost" type="button" onClick={() => setFiling(true)}>
            بفرست به کارِ استخراجگر
          </button>
        )}
      </div>

      {/* ── ۱ ــ رجیستری ─────────────────────────────── */}
      <div className="grid n2">
        <div>
          <h3>رجیستری</h3>
          {active ? (
            <>
              <p className="dim">
                نسخه‌ی {active.version} · {String(payload.tier ?? '—')} ·
                {' '}trust {String(payload.trust ?? '—')} ·
                {' '}{payload.enabled ? 'فعال' : 'خاموش'}
                {payload.aggregator ? ' · aggregator' : ''}
              </p>
              <p className="dim">
                extractor <span className="mono">{String(payload.extractor ?? '—')}</span>
                {' · '}goout_source{' '}
                <span className="mono">{String(payload.goout_source ?? '—')}</span>
                {' · '}منطقه{' '}
                <span className="mono">{String(payload.timezone ?? '—')}</span>
              </p>
              <Urls urls={urls} />
              {active.rationale ? (
                <p className="note" style={{ whiteSpace: 'pre-wrap' }}>{active.rationale}</p>
              ) : null}
              <div className="chiprow" style={{ marginTop: 8 }}>
                <button className="btn" type="button" onClick={() => setEditing(true)}>
                  ویرایش
                </button>
                {/*
                  ⚠️ تنها راهِ «کنار گذاشتنِ» یک منبعِ فعال همین است، و
                  اسمش هم عمداً «خاموش» است نه «حذف»: `source_versions`
                  نسخه‌دار است و هیچ‌چیز از آن پاک نمی‌شود — نسخه بازنشسته
                  می‌شود. ضمناً سند و mention و رخدادِ منتشرشده‌ی این منبع
                  همه به همین شناسه بندند.
                */}
                <button className="btn ghost" type="button" disabled={power.isPending}
                  onClick={() => power.mutate(payload.enabled !== true)}>
                  {payload.enabled ? 'خاموش کن' : 'روشن کن'}
                </button>
              </div>
            </>
          ) : (
            <p className="dim">هنوز ردیفی در رجیستری ندارد.</p>
          )}

          {d.domains.length > 0 ? (
            <p className="dim" style={{ marginTop: 6 }}>
              دامنه‌ها: {d.domains.map((x) => `${x.domain} (${x.city})`).join('، ')}
            </p>
          ) : null}
        </div>

        <div>
          <h3>تاریخچه‌ی نسخه‌ها</h3>
          {d.versions.length === 0 ? <p className="dim">—</p> : (
            <table className="tbl">
              <thead>
                <tr><th>نسخه</th><th>از</th><th>تا</th><th>هش</th></tr>
              </thead>
              <tbody>
                {d.versions.map((v: SourceVersionRow) => (
                  <tr key={v.version}>
                    <td>{v.version}{v.active ? ' ✅' : ''}</td>
                    <td className="dim">{when(v.created_at)}</td>
                    <td className="dim">{v.retired_at ? when(v.retired_at) : '—'}</td>
                    <td className="mono dim">{v.behavior_hash.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── ۲ ــ غربالگری، به‌ازای هر آدرس ──────────────── */}
      <h3 style={{ marginTop: 12 }}>غربالگری — هر آدرس</h3>
      {d.inspections.length === 0 ? (
        <p className="dim">
          هنوز بررسی نشده. تا وقتی حکمی نباشد، `promote` این ردیف را
          <b> رد نمی‌کند</b> — فقط کنار می‌گذارد.
        </p>
      ) : (
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                <th>آدرس</th><th>حکم</th><th>فهرست</th><th>گیت‌ها</th>
                <th>نمونه‌ی جزئیات</th><th>نسخه</th><th>کِی</th>
              </tr>
            </thead>
            <tbody>
              {d.inspections.map((i: InspectionRow) => (
                <tr key={`${i.source_id}|${i.url}`}>
                  <td>
                    <a className="mono" href={i.url} target="_blank" rel="noreferrer">{i.url}</a>
                    {i.notes?.length ? (
                      <div className="dim">{i.notes.join(' · ')}</div>
                    ) : null}
                  </td>
                  <td className="mono">{i.verdict}</td>
                  <td className="mono dim">{i.listing_verdict}</td>
                  <td className="dim">
                    <span className={i.looks_usable ? '' : 'bad'}>
                      {i.looks_usable ? '✅' : '❌'}
                    </span>
                    {' '}
                    <span className={i.has_events ? '' : 'bad'}>
                      {i.has_events ? '✅' : '❌'}
                    </span>
                  </td>
                  <td className="mono dim">{i.probe_verdict ?? '—'}</td>
                  <td className="num dim">{i.inspector_version}</td>
                  <td className="dim">{when(i.inspected_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── ۳ ــ پیش‌نویس ─────────────────────────────── */}
      <h3 style={{ marginTop: 12 }}>پیش‌نویس</h3>
      {d.draft ? (
        <>
          <p className="dim">
            <span className="mono">{d.draft.draft_id}</span> ·{' '}
            {d.draft.domain} · ساخته‌شده {when(d.draft.created_at)}
            {d.discovery ? (
              <> · از اجرای {d.discovery.id} (قالبِ {d.discovery.template_version}
                {d.discovery.model ? `، ${d.discovery.model}` : ''}،
                {' '}{d.discovery.candidates} کاندیدا)</>
            ) : ' · دستی'}
          </p>
          <p className="dim">
            حکمِ ترفیع: {d.draft.promote_outcome ?? 'هنوز هیچ'}
            {d.draft.promote_version != null ? ` (نسخه‌ی ${d.draft.promote_version})` : ''}
          </p>
          <Urls urls={d.draft.urls ?? []} />
          {/* ⚠️ پیش‌نویس **صف ندارد** و همان لحظه ثبت می‌شود؛ دکمه‌اش هم
              جداست تا با «ویرایش»ِ رجیستری (که در صف می‌نشیند) قاطی
              نشود. بیشترین کاربردش هم روشن است: آدرسی که مدل غلط حدس
              زده. */}
          <div className="chiprow" style={{ marginTop: 8 }}>
            <button className="btn" type="button" onClick={() => setEditingDraft(true)}>
              ویرایشِ پیش‌نویس
            </button>

            {/*
              ⚠️ «رد» جانشینِ «حذف» است، و بهتر از آن: ردیفِ پاک‌شده با
              کشفِ بعدی برمی‌گردد و دلیلش هم گم می‌شود. ردیفِ ردشده می‌ماند،
              دلیلش نوشته است، و از صفِ غربالِ شبانه بیرون است.

              ⚠️ فقط وقتی که ردیفِ رجیستری ندارد — روی منبعِ فعال، رجیستری
              بر همه‌چیز مقدم است و این دکمه هیچ اثری در جدول نمی‌گذاشت.
              خودِ RPC هم همین را رد می‌کند.
            */}
            {active ? null : (
              rejectionReason(row) ? (
                <button className="btn ghost" type="button" disabled={verdict.isPending}
                  onClick={() => verdict.mutate(null)}>
                  برگردان به پیش‌نویس
                </button>
              ) : (
                <button className="btn ghost" type="button" disabled={verdict.isPending}
                  onClick={() => setRejecting(true)}>
                  ردش کن
                </button>
              )
            )}
          </div>
          {/* یادداشتِ مدل: چرا اصلاً پیشنهادش داد. کوتاه ولی همان چیزی است
              که تصمیمِ آدم را می‌سازد. */}
          {typeof d.draft.payload?.notes === 'string' ? (
            <p className="note">{d.draft.payload.notes}</p>
          ) : null}
        </>
      ) : (
        <p className="dim">پیش‌نویسی ندارد — مستقیم در رجیستری ثبت شده.</p>
      )}

      {/* ── ۴ ــ سندها ────────────────────────────────── */}
      <h3 style={{ marginTop: 12 }}>آخرین سندها</h3>
      {d.documents.length === 0 ? <p className="dim">هیچ صفحه‌ای گرفته نشده.</p> : (
        <div className="scrollx">
          <table className="tbl">
            <thead><tr><th>آدرس</th><th>نوع</th><th className="num">وضعیت</th><th>کِی</th></tr></thead>
            <tbody>
              {d.documents.map((doc) => (
                <tr key={`${doc.url}|${doc.last_fetched_at}`}>
                  <td className="mono">{doc.url}</td>
                  <td className="dim">{doc.kind ?? '—'}</td>
                  <td className={`num${doc.http_status >= 400 ? ' bad' : ''}`}>
                    {doc.http_status}
                  </td>
                  <td className="dim">{when(doc.last_fetched_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filing ? (
        <TodoDialog
          label={row.source_label ?? row.draft_label ?? row.bare}
          seen={seenTodoNotes ?? []}
          busy={file.isPending}
          onClose={() => setFiling(false)}
          onSend={(kind, note) => { setFiling(false); file.mutate({ kind, note }); }} />
      ) : null}

      {rejecting && d.draft ? (
        <RejectDialog
          label={d.draft.label || row.bare}
          seen={seenReasons ?? []}
          busy={verdict.isPending}
          onClose={() => setRejecting(false)}
          onPick={(reason) => { setRejecting(false); verdict.mutate(reason); }} />
      ) : null}

      {editingDraft && d.draft ? (
        <Modal title={`ویرایشِ پیش‌نویسِ ${d.draft.draft_id}`}
          hint="همان لحظه ثبت می‌شود — پیش‌نویس صف ندارد."
          onClose={() => setEditingDraft(false)}>
          <DraftEditor draftId={d.draft.draft_id}
            current={{ ...(d.draft.payload ?? {}), label: d.draft.label,
                       urls: d.draft.urls ?? [] }}
            onDone={(m) => { setEditingDraft(false); onQueued(m); }} />
        </Modal>
      ) : null}

      {editing && active ? (
        <Modal title={`ویرایشِ ${row.bare}`}
          hint="تغییر در صف می‌نشیند و کرالر در اجرای بعدی اعمالش می‌کند."
          onClose={() => setEditing(false)}>
          <SourceEditor sourceId={row.bare} current={payload}
            onDone={(m) => { setEditing(false); onQueued(m); }} />
        </Modal>
      ) : null}
    </div>
  );
}
